import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { DashboardBasicViewRoute } from './dashboard-basic-view/dashboard-basic-view.route';
import { DashboardEconomyViewRoute } from './dashboard-economy-view/dashboard-economy-view.route';
import { DashboardLayoutRoute } from './dashboard-layout/dashboard-layout.route';
import { mapObject } from '@client/toolkit';
import { PermissionKeys } from '@client/api';
import { DashboardFiltersProvider } from './context/dashboard-filters.context';
import { DashboardEnergyViewRoute } from './dashboard-energy-view/dashboard-energy-view.route';
import { DashboardPortfolioViewRoute } from './dashboard-portfolio-view/dashboard-portfolio-view.route';
import { PackageTierProtectedRoute } from '../../components/package-tier-protected-route';
import { packageRulesConfig, useUserPackages } from '../../hooks/useUserPackages';
import { useUserPermission } from '../../hooks/useUserPermission';
import { AgreementsPaths } from '../agreements/agreements.router';

export const DashboardPaths = {
  Dashboard: '/dashboard',
  Management: '/dashboard/management',
  Economical: '/dashboard/economical',
  Energy: '/dashboard/energy',
  Portfolio: '/dashboard/portfolio',
};

const DashboardRouterPaths = mapObject(DashboardPaths, (val: string) => val.replace(DashboardPaths.Dashboard, ''));

function DashboardDefaultNavigate() {
  const { getAvailablePackages, isLoading } = useUserPackages();
  const location = useLocation();
  const availablePackage = getAvailablePackages();
  if (isLoading) return <></>;
  const { management, eos, portfolio } = availablePackage ?? {};
  if (portfolio) return <Navigate to={DashboardPaths.Portfolio + location.search} replace />;
  if (management) return <Navigate to={DashboardPaths.Management + location.search} replace />;
  if (eos) return <Navigate to={DashboardPaths.Energy + location.search} replace />;
  return <Navigate to={DashboardPaths.Management + location.search} replace />;
}

export function DashboardRouter() {
  const { hasAnyPermission, isLoading } = useUserPermission();

  if (isLoading) return null;

  const hasDashboardAccess = hasAnyPermission([PermissionKeys.User.DashboardView, PermissionKeys.Tenant.DashboardView]);

  if (!hasDashboardAccess) {
    return <Navigate to={AgreementsPaths.AgreementList} replace />;
  }

  return (
    <Routes>
      <Route element={<DashboardLayoutRoute />}>
        <Route
          path={DashboardRouterPaths.Management}
          element={
            <DashboardFiltersProvider>
              <PackageTierProtectedRoute requiredPermissions={packageRulesConfig.management}>
                <DashboardBasicViewRoute />
              </PackageTierProtectedRoute>
            </DashboardFiltersProvider>
          }
        />
        <Route
          path={DashboardRouterPaths.Economical}
          element={
            <DashboardFiltersProvider>
              <PackageTierProtectedRoute requiredPermissions={packageRulesConfig.economy}>
                <DashboardEconomyViewRoute />
              </PackageTierProtectedRoute>
            </DashboardFiltersProvider>
          }
        />
        <Route
          path={DashboardRouterPaths.Energy}
          element={
            <DashboardFiltersProvider dateParamsPrefix="eos">
              <PackageTierProtectedRoute requiredPermissions={packageRulesConfig.eos}>
                <DashboardEnergyViewRoute />
              </PackageTierProtectedRoute>
            </DashboardFiltersProvider>
          }
        />
        <Route
          path={DashboardRouterPaths.Portfolio}
          element={
            <DashboardFiltersProvider>
              <PackageTierProtectedRoute requiredPermissions={packageRulesConfig.portfolio}>
                <DashboardPortfolioViewRoute />
              </PackageTierProtectedRoute>
            </DashboardFiltersProvider>
          }
        />
        <Route path="*" element={<DashboardDefaultNavigate />} />
      </Route>
    </Routes>
  );
}
